import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import SideBarImage from '@/assets/sidebar-img.jpg';
import { useDarkMode } from '@/hooks/useDarkMode';
import { cn } from '@/utils/helper';
import PageLink from '@/utils/PageLink';

import BackButton from '../shared/Button/BackButton';
import Logo from '../shared/Logo/Logo';

interface AuthLayoutProps {
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  children: React.ReactNode;
  pageTitle?: string;
  isRightSideImg?: boolean;
  isBackButtonShow?: boolean;
  hideSidebar?: boolean;
}
const AuthLayout = ({
  children,
  title,
  description,
  pageTitle,
  isRightSideImg = false,
  isBackButtonShow = false,
  hideSidebar = false,
}: AuthLayoutProps) => {
  const { isDark } = useDarkMode();

  return (
    <>
      <NextSeo title={pageTitle} />
      <div className="relative flex h-screen">
        {!hideSidebar && (
          <div
            className={cn(
              'relative order-1 hidden w-[50%] overflow-hidden bg-[linear-gradient(180deg,_#004790_15%,_#003366_78%)] pt-24 lg:block',
              {
                'order-3': isRightSideImg,
              }
            )}>
            <div className=" ml-auto flex w-[90%] flex-col gap-2 text-white">
              <h2 className=" text-start text-4xl font-semibold">Your Smartest AI Assistant</h2>
              <p className=" max-w-lg text-base font-normal text-gray-200">
                Turn institutional knowledge into AI-powered expertise — create secure,
                domain-specific assistants for both customer-facing support and complex internal
                consultations
              </p>
            </div>
            <div className="ml-auto mt-10 flex w-[90%] items-end justify-end">
              <Image
                src={SideBarImage}
                alt="sidebar-img"
                className="h-full w-full rounded-s-lg object-cover"
              />
            </div>
          </div>
        )}
        <div className="auth-surface order-2 mx-auto my-auto flex h-fit max-w-[400px] flex-1 flex-col items-center justify-center">
          <div className="flex flex-1 flex-col items-center justify-start gap-6 px-2">
            <div className="flex w-full flex-col items-center justify-center gap-10">
              <div>
                <Link href={PageLink.home}>
                  <Logo size={160} className="block" />
                </Link>
              </div>

              <div className="flex w-full flex-col items-center justify-center gap-4">
                {typeof title === 'string' ? (
                  <p className="max-w-xs text-center text-3xl font-bold text-primary-700 md:max-w-sm md:text-4xl">
                    {title}
                  </p>
                ) : (
                  title
                )}
                {typeof description === 'string' ? (
                  <p
                    className={cn(
                      'max-w-sm text-center text-sm font-normal md:text-base',
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    )}>
                    {description}
                  </p>
                ) : (
                  description
                )}
              </div>
            </div>

            {children}
          </div>
          <p className={cn('mt-6 text-center text-sm', isDark ? 'text-gray-400' : 'text-gray-600')}>
            By continuing, you agree to our{' '}
            <Link href={PageLink.terms} className="font-semibold text-primary-600 underline">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link href={PageLink.privacy} className="font-semibold text-primary-600 underline">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
        {isBackButtonShow && (
          <div className="absolute left-6 top-6 md:left-8 md:top-8">
            <BackButton />
          </div>
        )}
      </div>
    </>
  );
};

export default AuthLayout;
